import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X, ShieldCheck, LayoutDashboard } from "lucide-react";
import { NAV } from "@/constants/testIds";
import BookCallButton from "@/components/site/BookCallButton";
import { useAuth } from "@/lib/auth";

const links = [
  { id: NAV.linkServices, label: "Services", href: "#services" },
  { id: NAV.linkHow, label: "How it works", href: "#how" },
  { id: NAV.linkPricing, label: "Pricing", href: "#pricing" },
  { id: "nav-link-book", label: "Book a call", href: "#book-call" },
  { id: NAV.linkFaq, label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();
  const bg = useTransform(scrollY, [0, 80], ["rgba(10,15,28,0)", "rgba(10,15,28,0.75)"]);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      style={{ backgroundColor: bg }}
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-xl transition-colors ${
        scrolled ? "border-b border-white/10" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <a href="#" data-testid={NAV.logo} className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#10B981] to-[#0e8f66] grid place-items-center shadow-lg shadow-emerald-500/30">
            <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display text-lg font-bold tracking-tight">
            Go <span className="text-[#D4AF37]">Higher</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={l.id}
              className="text-sm text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <BookCallButton
            variant="ghost"
            label="Book a call"
            showIcon={true}
            testId="nav-book-call"
            className="h-9 px-3 text-sm"
          />
          {user ? (
            <Link
              to="/dashboard"
              data-testid="nav-dashboard-link"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full border border-white/15 hover:border-white/30 text-white text-sm font-semibold transition-colors"
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
              Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin-link"
              className="text-white/70 hover:text-white text-sm font-semibold transition-colors"
            >
              Sign in
            </Link>
          )}
          <a
            href="#audit"
            data-testid={NAV.cta}
            className="px-5 h-10 inline-flex items-center rounded-full bg-[#10B981] text-white text-sm font-semibold hover:bg-[#0ea774] transition-colors shadow-lg shadow-emerald-500/25"
          >
            Get Free Audit
          </a>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 grid place-items-center rounded-md border border-white/10 text-white"
          aria-label="Toggle navigation"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#0A0F1C]/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white text-base"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#audit"
              onClick={() => setOpen(false)}
              className="px-5 h-11 inline-flex items-center justify-center rounded-full bg-[#10B981] text-white text-sm font-semibold"
            >
              Get Free Audit
            </a>
            {user ? (
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                className="px-5 h-11 inline-flex items-center justify-center gap-2 rounded-full border border-white/15 text-white text-sm font-semibold"
              >
                <LayoutDashboard className="w-4 h-4" />
                Go to dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                className="text-center text-white/80 hover:text-white text-sm font-semibold"
              >
                Sign in
              </Link>
            )}
            <BookCallButton
              variant="secondary"
              label="Book a free call"
              testId="nav-book-call-mobile"
              className="h-11"
            />
          </div>
        </div>
      )}
    </motion.header>
  );
}
